<template>
  <AcisoForm
    ref="form"
    :hide_footer="!ready"
    modal
    :ok_label="_('Create')"
    :panel_id="modal_id"
    :panel_title="modal_add_title"
    size="lg"
    @cancel="$emit('cancelled')"
    @submit="doSelect"
  >
    <div
      class="box-body"
      data-select2-id="6"
    >
      <form
        v-if="ready"
        data-vv-scope="solution_fork_import"
      >
        <FormFieldSelect
          v-if="!has_init_perimeter"
          v-model="current_obj.perimeter"
          v-validate="'required'"
          :allow_empty="false"
          :close-on-select="true"
          :error="errors.first('solution_fork_import' + '.perimeter')"
          :label="_('Perimeter')"
          name="perimeter"
          :options="perimeter_options"
          options_label="name"
          :placeholder="_('Select perimeter')"
          trackby="id"
        >
          <template #option="props">
            <b-badge
              v-if="props.option.identifier"
              class="d-inline"
              variant="primary"
              >{{ props.option.identifier }}</b-badge
            >&nbsp;
            <span class="option__title">{{ props.option.name }}</span>
          </template>
        </FormFieldSelect>

        <div v-if="current_obj.perimeter">
          <FormFieldSelect
            v-model="current_obj.group_user"
            v-validate="{ required: false }"
            :allow_empty="true"
            :custom-label="usersWithGroups"
            :disabled="current_operation === 'view'"
            :error="errors.first(scope + '.groups_users')"
            :help="
              type === 'run'
                ? _('The name of the person or the group in charge of this measure')
                : _('The name of the person or the group in charge of this action')
            "
            :label="_('Manager')"
            name="groups_users"
            :options="groups_users_f"
            options_label="name"
            :placeholder="_('Select group or user')"
            trackby="id"
            @change="setUserGroupDirtyFlag"
          >
            <template #option="props">
              <span
                v-if="props.option.is_group"
                class="d-inline"
                ><i class="far fa-users" />&nbsp;
                <span class="option__title">{{ props.option.name }}</span>
              </span>
              <span
                v-else
                class="d-inline"
                ><i class="far fa-user" />&nbsp;
                <span class="option__title">{{ props.option.name }}</span>
              </span>
              <span class="ms-times pull-right"><translate>Remove</translate></span>
            </template>
            <template #singleLabel="props">
              <span
                v-if="props.option.is_group"
                class="d-inline"
                ><i class="far fa-users" />&nbsp;
                <span class="option__title">{{ props.option.name }}</span>
              </span>
              <span
                v-else
                class="d-inline"
                ><i class="far fa-user" />&nbsp;
                <span class="option__title">{{ props.option.name }}</span>
              </span>
            </template>
          </FormFieldSelect>

          <FormFieldSelect
            v-if="type === 'build' && program_options.length > 0"
            v-model="current_obj.program"
            :allow_empty="false"
            :disabled="current_operation === 'view'"
            :error="errors.first(scope + '.program')"
            :label="_('Action plans')"
            name="program"
            :options="program_options"
            options_label="name"
            :placeholder="_('Select register')"
            trackby="id"
          />

          <FormFieldSelect
            v-if="type === 'run' && runtasks && runtasks.length > 0"
            v-model="current_obj.runbook"
            :allow_empty="false"
            :disabled="current_operation === 'view'"
            :error="errors.first(scope + '.runbook')"
            :label="_('Run book')"
            name="runbook"
            :options="runbook_options"
            options_label="name"
            :placeholder="_('Select run book')"
            trackby="id"
          />

          <b-row v-if="type === 'run'">
            <b-col
              v-if="current_solution_ && runtasks && runtasks.length > 0"
              cols="6"
            >
              <h5>
                <translate>Select runtasks</translate>
              </h5>
              <b-checkbox-group v-model="current_obj.selected_runtasks">
                <b-checkbox
                  v-for="runtask in runtasks"
                  :key="runtask.id"
                  class="w-100"
                  :value="runtask.id"
                >
                  <b-badge v-if="runtask.identifier">{{ runtask.identifier }}</b-badge>
                  {{ runtask.name }}
                </b-checkbox>
                <br />
              </b-checkbox-group>
              <FormFieldDate
                v-if="current_obj.selected_runtasks && current_obj.selected_runtasks.length > 0"
                ref="start_date"
                v-model="current_obj.runtasks_start_date"
                v-validate="{ required: true, date_format: 'dd/MM/yyyy' }"
                :disabled="current_operation === 'view'"
                :label="_('Start date')"
                name="start_date"
              />
            </b-col>

            <b-col
              v-if="current_solution_ && indicators_f && indicators_f.length > 0"
              cols="6"
            >
              <h5>
                <translate>Select indicators</translate>
              </h5>
              <b-checkbox-group v-model="current_obj.selected_indicators">
                <b-checkbox
                  v-for="indicator in indicators_f"
                  :key="indicator.id"
                  class="w-100"
                  :value="indicator.id"
                >
                  <b-badge v-if="indicator.identifier">{{ indicator.identifier }}</b-badge>
                  {{ indicator.name }}
                </b-checkbox>
                <br />
              </b-checkbox-group>
            </b-col>
          </b-row>
        </div>
      </form>
      <p
        v-else
        class="text-center"
      >
        <i class="far fa-circle-notch fa-spin fa-2x" />
      </p>
    </div>
  </AcisoForm>
</template>

<script>
import AcisoForm from '@/components/layout/AcisoForm.vue'
import FormFieldSelect from '@/components/ui/FormFieldSelect.vue'
import EditMixin from '@/mixins/EditMixin'
import FormFieldDate from '@/components/ui/FormFieldDate.vue'
import { getOptionsMixin } from '@/services/GetOptionsMixin.services'
import { useReactiveSet } from '@/composables/UseReactiveSet.composables'

export default {
  name: 'SolutionForkSelect',
  components: { FormFieldDate, FormFieldSelect, AcisoForm },
  mixins: [EditMixin],
  props: {
    type: {
      type: String,
      default: '',
    },
    do_post: {
      type: Boolean,
      default: false,
    },
    current_solution_: Object,
  },
  data: function () {
    return {
      ready: false,
      runtasks: [],
      indicators: [],
      perimeter_options: [],
      runtasks_start_date: this.$moment().format('DD/MM/YYYY'),
      has_init_perimeter: false,
      api_endpoint: 'remediation',
      program_options: [],
      runbook_options: [],
    }
  },
  computed: {
    modal_add_title: function () {
      let t
      if (this.type === 'build') {
        t = this._('Add implementation action')
      } else {
        t = this._('Implement the measure')
      }
      if (this.current_solution_) {
        t += ' ' + this.current_solution_.name
      }
      return t
    },
    indicators_f: function () {
      if (this.current_solution_) {
        if (this.current_solution_.public === true) {
          return this.indicators.filter((e) => e.public === true)
        } else {
          return this.indicators
        }
      }
      return []
    },
  },
  mounted() {
    this.sortGroups()
  },
  methods: {
    usersWithGroups({ groups_users }) {
      return `${groups_users.name}`
    },
    preFormSubmit(obj) {
      this.setUserGroupSubmit(obj)
      if (!this.current_obj.perimeter) {
        this.$toast.alert(this._('Missing perimeter'))
        return
      }
      if (this.current_solution_.solution && this.current_solution_.solution.id) {
        obj.id = this.current_solution_.solution.id
      } else if (this.current_solution_.solution_id) {
        obj.id = this.current_solution_.solution_id
      } else if (this.current_solution_.id) {
        obj.id = this.current_solution_.id
      }
      obj.remediation_id = this.current_solution_.remediation_id
      obj.runtasks = this.current_obj.selected_runtasks
      obj.indicators = this.current_obj.selected_indicators
      obj.perimeter_id = this.current_obj.perimeter.id
      obj.runbook_id = this.current_obj.runbook ? this.current_obj.runbook.id : -1
      obj.runtasks_start_date = this.current_obj.runtasks_start_date
      delete obj.selected_runtasks
      delete obj.selected_indicators
      delete obj.perimeter
      delete obj.runbook
      delete obj.group_user
      if (this.current_obj.user) {
        obj.user = this.current_obj.user
      } else if (this.current_obj.group) {
        obj.group = this.current_obj.group
      }
      this.ready = false
      return obj
    },
    postSubmit() {
      this.ready = true
    },
    doSelect() {
      const _this = this
      if (this.do_post) {
        this.submit()
      } else if (this.current_obj.perimeter && this.current_obj.perimeter.id) {
        this.setUserGroupSubmit(this.current_obj)
        this.$emit('selected', this.current_obj)
        this.$refs.form.hide()
      } else {
        this.$validator.validateAll('solution_fork_import').then((res) => {
          if (!res) {
            _this.$log.debug(this.$validator.errors)
          }
        })
      }
    },
    preFormHandler() {
      const _this = this
      this.ready = false
      this.runtasks = []
      this.indicators = []
      this.$log.debug(this.current_solution_)
      if (!this.current_solution_) {
        this.$log.warn('No solution...')
        return
      }
      if (this.current_solution_ && this.current_solution_.id && this.type === 'run') {
        this.$http
          .get('/solution/' + this.current_solution_.id + '/rti')
          .then((resp) => {
            if (resp.data.ok) {
              _this.runtasks = resp.data.object.runtasks
              _this.indicators = resp.data.object.indicators
              _this.ready = true
            } else {
              _this.ready = true
              _this.$log.debug(resp.data)
            }
          })
          .catch((err) => {
            _this.ready = true
            this.apiError(err, false)
          })
      } else {
        _this.ready = true
      }
      if (this.current_solution_.solution && this.current_solution_.solution.id) {
        useReactiveSet(this.current_obj, 'id', this.current_solution_.solution.id)
      } else if (this.current_solution_.solution_id) {
        useReactiveSet(this.current_obj, 'id', this.current_solution_.solution_id)
      } else if (this.current_solution_.id) {
        useReactiveSet(this.current_obj, 'id', this.current_solution_.id)
      }
      useReactiveSet(this.current_obj, 'selected_indicators', [])
      useReactiveSet(this.current_obj, 'selected_runtasks', [])
      useReactiveSet(this.current_obj, 'runtasks_start_date', this.$moment().format('DD/MM/YYYY'))
      if (this.type === 'build') {
        if (this.program_options.length > 0) {
          useReactiveSet(this.current_obj, 'program', this.program_options[0])
        } else {
          useReactiveSet(this.current_obj, 'program', null)
        }
      } else if (this.type === 'run') {
        const default_prog = _this.runbook_options.find((opt) => opt.identifier === '_DEF')
        if (default_prog) {
          useReactiveSet(_this.current_obj, 'runbook', default_prog)
        } else {
          useReactiveSet(_this.current_obj, 'runbook', _this.runbook_options[0])
        }
      }
      if (this.current_obj.perimeter && this.current_obj.perimeter.id) {
        this.has_init_perimeter = true
      } else {
        this.has_init_perimeter = false
      }
    },
    async getFormOptions() {
      const [{ options: perimeterOptions }, { options: programOptions }, { options: runbookOptions }] =
        await Promise.all([
          getOptionsMixin('user/perimeters'),
          getOptionsMixin('remediation/programs'),
          getOptionsMixin('runtask/runbooks'),
        ])

      this.perimeter_options = perimeterOptions
      this.program_options = programOptions
      this.runbook_options = runbookOptions
    },
  },
}
</script>
